import { convertToCurrencyUnit } from "./currency";
import { DROP_DECIMAL_THRESHOLD } from "../numberConstants";
import { createDecimal, subtractValues, divideValues } from "./math";

/**
 * This function processes the table data and checks if the specified columns contain decimal values above a certain threshold.
 *
 * @param {Array} tableData - Array of table rows where each row contains column data.
 * @param {Array} columnNames - Array of column names that should be checked for decimal values.
 * @returns {Object} - An object indicating which columns contain decimal values greater than the threshold.
 */
export const dropDecimalColumns = (tableData, columnNames) => {
  let decimalColumn = {};
  // initialise all as false
  for (let name of columnNames) {
    decimalColumn[name] = false;
  }
  if (tableData) {
    for (let line of tableData) {
      for (let name of columnNames) {
        if (dropDecimal(line[name])) {
          decimalColumn[name] = true;
        }
      }
    }
  }

  return decimalColumn;
};

/**
 * This function determines if the given number exceeds the threshold for being considered a decimal.
 *
 * @param {number} num - The number to be checked.
 * @returns {boolean} - Returns true if the number exceeds the threshold, false otherwise.
 */
export const dropDecimal = (num) => {
  const number = createDecimal(num);
  return (
    number.gte(DROP_DECIMAL_THRESHOLD) ||
    number.lte(DROP_DECIMAL_THRESHOLD.neg())
  );
};

/**
 * This function formats a header title, optionally adding a currency symbol if enabled.
 *
 * @param {string} headerTitle - The title of the header (default is an empty string).
 * @param {string} currencySymbol - The currency symbol to append (default is an empty string).
 * @param {boolean} enableHeaderCurrency - Flag to enable or disable the addition of the currency symbol (default is false).
 * @returns {string} - The formatted header title, with or without the currency symbol.
 */
export const headerFormatter = (
  headerTitle = "",
  currencySymbol = "",
  enableHeaderCurrency = false,
) => {
  if (enableHeaderCurrency) {
    return `${headerTitle} (${currencySymbol})`;
  }
  return headerTitle;
};

/**
 * This function calculates the net claim value by subtracting the claimed amount from the total claim value.
 * It then formats the result into a currency format.
 *
 * @param {string} id - The unique identifier of the claim item.
 * @param {Array} claimList - The list of claims containing the values to calculate the net claim.
 * @param {number} claimed - The already claimed amount.
 * @param {boolean} dropDecimal - Flag to determine whether to drop decimals in the result.
 * @param {boolean} showSymbol - Flag to determine whether to show the currency symbol.
 * @returns {string} - The formatted net claim value as a currency string.
 */
export const calculateNetClaim = (
  id,
  claimList,
  claimed,
  dropDecimal,
  showSymbol,
) => {
  var value = 0;

  if (claimList && id) {
    if (Array.isArray(claimList)) {
      for (var i in claimList) {
        if (claimList[i].lineitem_id === id) {
          value = claimList[i].value;
          break;
        }
      }
    } else {
      value = claimList[id]?.value || 0;
    }
  }

  value = createDecimal(value);
  claimed = createDecimal(claimed);
  let result = "$0.00";
  let total = subtractValues([value, claimed]);
  total = divideValues([total, 100]);
  result = convertToCurrencyUnit(total, showSymbol, dropDecimal);
  return result;
};
